import Grid from "@mui/material/Grid";
import { useMessage } from "hooks/intl";
import { kebabCase } from "lodash";
import { OLPaper } from "OLPaper";
import { useMemo } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { BackendMemberAddressModel } from "types/members";
import { KeyValuePair } from "utilities/objects";
import { useStyles } from "./SingleRewardFulfillment.styles";

interface RewardFulfillmentAddressCardProps {
  isLoading: boolean;
  address?: BackendMemberAddressModel;
}

export function RewardFulfillmentAddressCard({
  isLoading,
  address,
}: RewardFulfillmentAddressCardProps) {
  const classes = useStyles();
  const { message } = useMessage();

  const leftColumnFields = useMemo(
    () => [
      {
        key: message({ id: "streetName" }),
        value: address?.street,
      },
      {
        key: message({ id: "flatUnitName" }),
        value: address?.address2,
      },
      {
        key: message({ id: "city" }),
        value: address?.city,
      },
      {
        key: message({ id: "country" }),
        value: address?.country,
      },
    ],
    [address, message],
  );

  const rightColumnFields = useMemo(
    () => [
      {
        key: message({ id: "buildingName" }),
        value: address?.address1,
      },
      {
        key: message({ id: "postalCode" }),
        value: address?.postal,
      },
      {
        key: message({ id: "stateProvince" }),
        value: address?.province,
      },
    ],
    [address, message],
  );

  function renderColumn(columnFields: KeyValuePair[]) {
    return columnFields.map(({ key, value }: KeyValuePair) => (
      <div
        className={classes.detailsItem}
        data-test-id={`single-reward-fulfillment-${kebabCase(key)}`}
      >
        <div className={classes.smallText}>{key}</div>
        <div className={classes.largeText}>
          {isLoading ? <Skeleton width={100} /> : value || "-"}
        </div>
      </div>
    ));
  }

  return (
    <>
      <div className={classes.sectionHeader}>
        {message({ id: "addressDetails" })}
      </div>
      <OLPaper className={classes.detailsCard}>
        <Grid container spacing={10}>
          <Grid item xs={12} sm={12} md={6}>
            {renderColumn(leftColumnFields)}
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            {renderColumn(rightColumnFields)}
          </Grid>
        </Grid>
      </OLPaper>
    </>
  );
}
